import {
  CART_REQUEST,
  CART_SUCCESS,
  CART_FAIL,
  CART_ADD_ITEM_REQUEST,
  CART_ADD_ITEM_SUCCESS,
  CART_ADD_ITEM_FAIL,
  CART_UPDATE_ITEM_REQUEST,
  CART_UPDATE_ITEM_SUCCESS,
  CART_UPDATE_ITEM_FAIL,
  CART_REMOVE_ITEM_REQUEST,
  CART_REMOVE_ITEM_SUCCESS,
  CART_REMOVE_ITEM_FAIL,
  CART_CLEAR_REQUEST,
  CART_CLEAR_SUCCESS,
  CART_CLEAR_FAIL,
  CART_CLEAR_ITEMS,
} from '../constants/cartConstants';
import api from '../utils/api';

// Get cart (for authenticated users)
export const getCart = () => async (dispatch, getState) => {
  try {
    dispatch({ type: CART_REQUEST });

    const {
      auth: { token, isAuthenticated },
    } = getState();

    if (!isAuthenticated) {
      // For guests, use local storage cart
      const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
      dispatch({
        type: CART_SUCCESS,
        payload: { items: cartItems, cart: null },
      });
      return;
    }

    const { data } = await api.get('/cart');

    dispatch({
      type: CART_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: CART_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

// Add item to cart
export const addToCart = (productId, quantity) => async (dispatch, getState) => {
  try {
    dispatch({ type: CART_ADD_ITEM_REQUEST });

    const {
      auth: { token, isAuthenticated },
    } = getState();

    // Get product details first
    const { data: product } = await api.get(`/products/${productId}`);

    if (!isAuthenticated) {
      // For guests, store in local storage
      const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
      
      // Check if item exists
      const existItem = cartItems.find(x => x.product_id === productId);
      
      let updatedItem;
      
      if (existItem) {
        // Update quantity
        updatedItem = {
          ...existItem,
          quantity: existItem.quantity + quantity
        };
        
        const updatedItems = cartItems.map(item =>
          item.product_id === productId ? updatedItem : item
        );
        
        localStorage.setItem('cartItems', JSON.stringify(updatedItems));
      } else {
        // Add new item
        updatedItem = {
          id: `local-${Date.now()}`,
          product_id: productId,
          quantity,
          price: product.price,
          name: product.name,
          chinese_name: product.chinese_name,
          image_url: product.image_url,
          stock_quantity: product.stock_quantity
        };
        
        localStorage.setItem('cartItems', JSON.stringify([...cartItems, updatedItem]));
      }
      
      dispatch({
        type: CART_ADD_ITEM_SUCCESS,
        payload: {
          item: updatedItem,
          isAuthenticated: false
        },
      });
      
      return;
    }

    // For authenticated users, use API
    const { data } = await api.post(
      '/cart/items',
      { product_id: productId, quantity }
    );

    dispatch({
      type: CART_ADD_ITEM_SUCCESS,
      payload: {
        item: data.item,
        isAuthenticated: true
      },
    });
  } catch (error) {
    dispatch({
      type: CART_ADD_ITEM_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

// Update cart item quantity
export const updateCartItemQuantity = (itemId, quantity) => async (dispatch, getState) => {
  try {
    dispatch({ type: CART_UPDATE_ITEM_REQUEST });

    const {
      auth: { token, isAuthenticated },
    } = getState();

    if (!isAuthenticated) {
      // For guests, update local storage
      const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
      
      const item = cartItems.find(x => x.id === itemId);
      if (!item) {
        throw new Error('Item not found');
      }
      
      // Update quantity
      const updatedItem = {
        ...item,
        quantity
      };
      
      const updatedItems = cartItems.map(item =>
        item.id === itemId ? updatedItem : item
      );
      
      localStorage.setItem('cartItems', JSON.stringify(updatedItems));
      
      dispatch({
        type: CART_UPDATE_ITEM_SUCCESS,
        payload: {
          item: updatedItem,
          isAuthenticated: false
        },
      });
      
      return;
    }

    // For authenticated users, use API
    const { data } = await api.put(
      `/cart/items/${itemId}`,
      { quantity }
    );

    dispatch({
      type: CART_UPDATE_ITEM_SUCCESS,
      payload: {
        item: data.item,
        isAuthenticated: true
      },
    });
  } catch (error) {
    dispatch({
      type: CART_UPDATE_ITEM_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

// Remove item from cart
export const removeFromCart = (itemId) => async (dispatch, getState) => {
  try {
    dispatch({ type: CART_REMOVE_ITEM_REQUEST });

    const {
      auth: { isAuthenticated },
    } = getState();

    if (!isAuthenticated) {
      // For guests, update local storage
      const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
      const updatedItems = cartItems.filter(item => item.id !== itemId);
      localStorage.setItem('cartItems', JSON.stringify(updatedItems));
      
      dispatch({
        type: CART_REMOVE_ITEM_SUCCESS,
        payload: {
          itemId,
          isAuthenticated: false
        },
      });
      
      return;
    }

    // For authenticated users, use API
    await api.delete(`/cart/items/${itemId}`);

    dispatch({
      type: CART_REMOVE_ITEM_SUCCESS,
      payload: {
        itemId,
        isAuthenticated: true
      },
    });
  } catch (error) {
    dispatch({
      type: CART_REMOVE_ITEM_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

// Clear cart
export const clearCart = () => async (dispatch, getState) => {
  try {
    dispatch({ type: CART_CLEAR_REQUEST });

    const {
      auth: { isAuthenticated },
    } = getState();

    if (!isAuthenticated) {
      // For guests, clear local storage
      localStorage.removeItem('cartItems');
      
      dispatch({
        type: CART_CLEAR_SUCCESS,
        payload: { isAuthenticated: false },
      });
      
      return;
    }

    // For authenticated users, use API
    await api.delete('/cart');

    dispatch({
      type: CART_CLEAR_SUCCESS,
      payload: { isAuthenticated: true },
    });
  } catch (error) {
    dispatch({
      type: CART_CLEAR_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

// Clear cart after successful order creation
export const clearCartItems = () => (dispatch) => {
  dispatch({ type: CART_CLEAR_ITEMS });
};
